import { useCallback, useEffect, useRef, useState, type FC } from "react";
import { omit } from "es-toolkit";
import { NativeSelect } from "@mantine/core";
import { get } from "es-toolkit/compat";
import { FormattedMessage, useIntl } from "react-intl";
import { useElements } from "@stripe/react-stripe-js";
import type { useForm } from "@mantine/form";
import type {
  StripeAddressElement,
  StripeAddressElementChangeEvent,
  StripeAddressElementOptions,
  StripeTaxIdElement,
  StripeTaxIdElementOptions,
  TaxIdType,
} from "@stripe/stripe-js";
import type { BillingAddress } from "@project/common";

interface AddressFormProps {
  defaultAddress?: BillingAddress | null;
  getInputProps: ReturnType<typeof useForm>["getInputProps"];
  isPending: boolean;
  isCompany: boolean;
}

const personAddressFormOptions: StripeAddressElementOptions = {
  display: {
    name: "full" as const,
  },
  mode: "billing" as const,
  fields: {
    phone: "always",
  },
};

const orgAddressFormOptions: StripeAddressElementOptions = {
  mode: "billing" as const,
  display: {
    name: "organization" as const,
  },
  fields: {
    phone: "always",
  },
};

const taxIdFormOptions: StripeTaxIdElementOptions = {
  visibility: "auto",
  fields: {
    businessName: "never",
  },
  validation: {
    taxId: {
      required: "auto",
    },
  },
};

const defaultAddressInit: Partial<BillingAddress> = {};

/**
 * List of countries that are supported by Stripe TaxIdElement
 * This is workaround for visibility: auto not working in Stripe TaxIdElement
 */
const SUPPORT_VAT = [
  "AL", "AO", "AM", "AW", "AU", "AT", "AZ", "BS", "BH", "BD",
  "BB", "BY", "BE", "BJ", "BA", "BG", "BF", "KH", "CM", "CA",
  "CV", "CL", "CD", "CR", "HR", "CY", "CZ", "DK", "EC", "EG",
  "EE", "ET", "FI", "FR", "GE", "DE", "GR", "GN", "HU", "IS",
  "IN", "IE", "IT", "KZ", "KE", "KG", "LA", "LV", "LI", "LT",
  "LU", "MT", "MR", "MX", "MD", "ME", "MA", "NP", "NL", "NZ",
  "NG", "MK", "NO", "OM", "PE", "PH", "PL", "PT", "RO", "RU",
  "SA", "SN", "RS", "SG", "SK", "SI", "ZA", "KR", "ES", "SR",
  "SE", "CH", "TW", "TJ", "TZ", "TH", "TR", "UG", "UA", "AE",
  "XI", "GB", "UY", "UZ", "ZM", "ZW",
];

export const AddressForm: FC<AddressFormProps> = ({ getInputProps, defaultAddress, isCompany }) => {
  const intl = useIntl();
  const elements = useElements();

  const orAddress = defaultAddress ?? defaultAddressInit;
  const initialCountry = get(orAddress, "address.country");
  // @ts-expect-error Marcin knows
  const address = omit(orAddress, ["type", "taxId", "taxIdType", "externalTaxIdType", "vat"]);

  const [country, setCountry] = useState(initialCountry ?? "");

  const tax = {
    taxId: get(orAddress, "taxId"),
    taxIdType: get(orAddress, "taxIdType") as TaxIdType,
  };

  const addressContainerRef = useRef<HTMLDivElement>(null);
  const taxIdContainerRef = useRef<HTMLDivElement>(null);
  const addressElementRef = useRef<StripeAddressElement | null>(null);
  const taxIdElementRef = useRef<StripeTaxIdElement | null>(null);

  const handleAddressChange = useCallback((event: StripeAddressElementChangeEvent) => {
    setCountry(event.value.address.country);
  }, []);

  useEffect(() => {
    if (!elements || !addressContainerRef.current) return;

    if (addressElementRef.current) {
      addressElementRef.current.clear();
      addressElementRef.current.unmount();
      addressElementRef.current.destroy();
    }

    const options = isCompany ? orgAddressFormOptions : personAddressFormOptions;
    const addressElement = elements.create("address", {
      ...options,
      defaultValues: address,
    });

    addressElement.on("change", handleAddressChange);
    addressElement.on("loaderror", ({ error }) => {
      logger.error({ error }, "Failed to load addressElement");
    });

    addressElement.mount(addressContainerRef.current);
    addressElementRef.current = addressElement;

    return () => {
      addressElement.unmount();
      addressElement.destroy();
      addressElementRef.current = null;
    };
  }, [elements, isCompany]);

  useEffect(() => {
    if (!elements || !taxIdContainerRef.current) return;

    /**
     * Unmount to ensure that previous values are cleared
     */
    if (taxIdElementRef.current) {
      taxIdElementRef.current.clear();
      taxIdElementRef.current.unmount();
      taxIdElementRef.current.destroy();
    }

    if (!isCompany || !SUPPORT_VAT.includes(country)) {
      return;
    }

    const taxIdElement = elements.create("taxId", {
      ...taxIdFormOptions,
      defaultValues: initialCountry === country ? tax : undefined,
    });

    taxIdElement.on("loaderror", ({ error }) => {
      logger.error({ error }, "Failed to load taxIdElement");
    });

    taxIdElement.mount(taxIdContainerRef.current);
    taxIdElementRef.current = taxIdElement;

    return () => {
      if (taxIdElementRef.current) {
        taxIdElementRef.current.clear();
        taxIdElementRef.current.unmount();
        taxIdElementRef.current.destroy();
        taxIdElementRef.current = null;
      }
    };
  }, [elements, isCompany, country, initialCountry]);

  return (
    <>
      <NativeSelect
        label={<FormattedMessage id="label.accountType" defaultMessage="Account type" />}
        data={[
          { label: intl.formatMessage({ id: "label.personal", defaultMessage: "Personal" }), value: "personal" },
          { label: intl.formatMessage({ id: "label.business", defaultMessage: "Business" }), value: "business" },
        ]}
        {...getInputProps("type")}
      />

      <div ref={addressContainerRef} />
      <div ref={taxIdContainerRef} />
    </>
  );
};
